import { Component, EventEmitter, Inject, OnInit, Output, ChangeDetectionStrategy } from '@angular/core';
import { FormBuilder, FormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { MatDialog, MatDialogConfig, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { CloseCase } from '@core/models/leave-admin/close-case.model';
import { Subject, Observable } from 'rxjs';
import { UnsavedChangesComponent } from '../unsaved-changes/unsaved-changes.component';
import { CloseCasePost } from '@core/models/leave-admin/close-case-post.model';
import { SnackbarService, NotificationService, ErrorService, LayoutService } from '@core/services';
import { LeaveAdminService } from '@core/services/leave-admin.service';
import { indicate, closeDialogOnEscape } from '@shared/helpers';
import { finalize, takeUntil } from 'rxjs/operators';
import { CaseDetailsWithEmailResult, StiiraError } from '@core/models';
import { position, onDragEnded } from '@shared/helpers/dialog-drag-constraints';
import { LeaveAdminStoreService } from '@core/services/leave-admin-store.service';
import { PendingActionsAlertComponent } from '../pending-actions-alert/pending-actions-alert.component';
import { EmailResult } from '@core/enums';

@Component({
    selector: 'app-close-case',
    templateUrl: './close-case.component.html',
    styleUrls: ['./close-case.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class CloseCaseComponent implements OnInit {
  static readonly componentName = 'CloseCaseComponent';
  
  @Output() isEditing = new EventEmitter<boolean>();
  public form: UntypedFormGroup;
  public canClose: boolean;
  public isSubmitting$: Subject<boolean>;

  public position = position;
  public onDragEnded = onDragEnded;
  
  private formInitValues: any;
  private closeCasePost: CloseCasePost;
  private destroy$: Subject<void> = new Subject<void>();

  get noChanges(): boolean {
    return JSON.stringify(this.form.value) === JSON.stringify(this.formInitValues);
  }

  get closeReasonId(): FormControl {
    return this.form.controls.closeReasonId as FormControl;
  }

  get closeDate(): FormControl {
    return this.form.controls.closeDate as FormControl;
  }

  get comments(): FormControl {
    return this.form.controls.comments as FormControl;
  }

  get sendNotification(): FormControl {
    return this.form.controls.sendNotification as FormControl;
  }

  public isMobile$: Observable<boolean>;
  
  constructor(
    @Inject(MAT_DIALOG_DATA)
    public data: { ccdata: CloseCase; sysText: any },
    private closeCaseDialogComponent: MatDialogRef<CloseCaseComponent>,
    private service: LeaveAdminService,
    private dialog: MatDialog,
    private fb: FormBuilder,
    private snackbar: SnackbarService,
    private notificationService: NotificationService,
    private errorService: ErrorService,
    private layoutService: LayoutService,
    private leaveAdminStore: LeaveAdminStoreService
  ) {
    this.isSubmitting$ = new Subject();
    this.form = this.fb.group({
      caseId: [this.data.ccdata.caseId],
      closeReasonId: [null, Validators.required],
      closeDate: [(new Date()), Validators.required],
      comments: [null],
      sendNotification: false
    });
  }

  ngOnInit(): void {
    this.isMobile$ = this.layoutService.isMobile$();

    closeDialogOnEscape(this.closeCaseDialogComponent, this.isSubmitting$, () => this.cancel());

    this.formInitValues = { ...this.form.value };
    this.canClose = this.data.ccdata.canClose;

    this.comments.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(()=> {
        if (!this.noChanges) {
          this.isEditing.emit(true);
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  public closeCase(): void {
    if (this.leaveAdminStore.isActionRunning) {
      this.openPendingChangesDialog();
      return;
    }

    this.closeCasePost = {
      caseId: this.data.ccdata.caseId,
      closeDate: this.closeDate.value,
      closeReasonId: this.closeReasonId.value,
      comments: this.comments.value,
      sendNotification: !this.data.ccdata.isAnonymous && this.data.ccdata.showNotificationToggle
        ? this.sendNotification.value
        : null
      };

    this.service.postCloseCase(this.closeCasePost)
      .pipe(
        indicate(this.isSubmitting$),
        finalize(() => { }))
      .subscribe(
        (res: CaseDetailsWithEmailResult) => {
          this.closeCaseDialogComponent.close(res.leaveCaseDetails);

          if (res.emailResult == EmailResult.success) {
            let snackText =  this.data.sysText.closeCase.caseClosedWithEmailSuccess;
            this.snackbar.open(snackText, this.data.sysText.closeCase.dismiss);
          }
          else if (res.emailResult == EmailResult.successWithFailure) {
            let toastText = this.data.sysText.closeCase.caseClosedWithEmailSuccessFailure;
            this.notificationService.showErrorToast(toastText);
          }
          else if (res.emailResult == EmailResult.failure) {
            let toastText = this.data.sysText.closeCase.caseClosedWithEmailFailure;
            this.notificationService.showErrorToast(toastText);
          }  
          else {
            let snackText = this.data.sysText.closeCase.caseClosedWithoutEmail;
            this.snackbar.open(snackText, this.data.sysText.closeCase.dismiss);
          }  
        },
        (err: StiiraError) => {
            this.errorService.setFormModelStateErrors(this.form, err.modelStateErrors);
          }
      );
  }

  public cancel(): void {
    if (this.noChanges) {
      this.closeCaseDialogComponent.close();
    } else {
      this.openUnsavedChangesDialog();
    }
  }

  public closeReasonChangeHandler (): void {
    this.isEditing.emit(true);
  }

  public closeDateChangeHandler(): void {
    this.isEditing.emit(true);
  }

  private  openUnsavedChangesDialog(): void {
    const dialogConfig: MatDialogConfig = {
      id: UnsavedChangesComponent.componentName,
      width: '300px',
      data: this.data.sysText.leavePage
    };
  
    this.dialog.open(UnsavedChangesComponent, dialogConfig)
      .afterClosed()
      .subscribe((res: boolean) => {
        if (res) {
          this.closeCaseDialogComponent.close();
        }
      });
  }

  private openPendingChangesDialog(): void {
    const dialogConfig: MatDialogConfig = {
      id: PendingActionsAlertComponent.componentName,
      width: '300px'
    };

    this.dialog.open(PendingActionsAlertComponent, dialogConfig)
      .beforeClosed()
      .subscribe((res: boolean) => {
      if (res) {
        this.closeCase();
      }
    });
  }
}